import React from 'react';
import { CheckCircle } from 'lucide-react';
import { ACCENTS, Icon, sectionBg } from './theme';

const SplitPanel = ({ panel }) => (
  <div className={`bg-gradient-to-br ${panel.gradient} p-8 rounded-3xl text-white`}>
    {panel.icon && <Icon name={panel.icon} className="w-12 h-12 mb-6" />}
    <h3 className="text-2xl font-bold mb-6">{panel.title}</h3>
    <p className={`${panel.subClass} mb-6`}>{panel.desc}</p>
    {panel.stats && (
      <div className="grid grid-cols-2 gap-4">
        {panel.stats.map((s) => (
          <div key={s.label} className="bg-white/10 backdrop-blur p-4 rounded-xl">
            <p className={`text-3xl font-bold ${panel.statClass}`}>{s.value}</p>
            <p className={`text-sm ${panel.subClass}`}>{s.label}</p>
          </div>
        ))}
      </div>
    )}
    {panel.chips && (
      <div className={`grid grid-cols-2 ${panel.chipIcon === 'CheckCircle' ? 'gap-4' : 'gap-3'}`}>
        {panel.chips.map((chip) => (
          <div key={chip} className={panel.chipIcon === 'CheckCircle' ? 'flex items-center gap-2 text-sm' : 'bg-white/10 backdrop-blur p-3 rounded-xl flex items-center gap-2'}>
            <Icon name={panel.chipIcon} className={`w-4 h-4 ${panel.chipIconClass} flex-shrink-0`} />
            <span className="text-sm">{chip}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const SplitSection = ({ section, accent }) => {
  const t = ACCENTS[accent];
  const textCol = (
    <div>
      <span className={`${t.label} font-semibold uppercase tracking-wide text-sm`}>{section.label}</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-6">{section.heading}</h2>
      <p className="text-lg text-gray-600 mb-6">{section.desc}</p>
      {section.itemVariant === 'chips' ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {section.items.map((item) => (
            <div key={item.title} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <Icon name={section.itemIcon} className={`w-5 h-5 ${t.icon} flex-shrink-0`} />
              <span className="text-gray-700">{item.title}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {section.items.map((item) => (
            <div key={item.title} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className={`${t.iconBox} p-2 rounded-lg`}>
                <Icon name={section.itemIcon} className={`w-5 h-5 ${t.icon}`} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{item.title}</h4>
                {item.desc && <p className="text-sm text-gray-600">{item.desc}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <section className={sectionBg(section.bg)}>
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {section.reverse ? (
            <>
              <SplitPanel panel={section.panel} />
              {textCol}
            </>
          ) : (
            <>
              {textCol}
              <SplitPanel panel={section.panel} />
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const PANEL_THEMES = {
  blue: { bg: 'from-blue-50 to-blue-100', iconBox: 'bg-blue-600', itemIcon: 'text-blue-600' },
  emerald: { bg: 'from-emerald-50 to-emerald-100', iconBox: 'bg-emerald-600', itemIcon: 'text-emerald-600' },
  amber: { bg: 'from-amber-50 to-amber-100', iconBox: 'bg-amber-600', itemIcon: 'text-amber-600' },
};

export const DuoPanels = ({ section }) => (
  <section className={sectionBg(section.bg)}>
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-12">
        {section.panels.map((panel) => {
          const pt = PANEL_THEMES[panel.theme];
          return (
            <div key={panel.title} className={`bg-gradient-to-br ${pt.bg} p-8 rounded-3xl`}>
              <div className={`${pt.iconBox} w-16 h-16 rounded-2xl flex items-center justify-center mb-6`}>
                <Icon name={panel.icon} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{panel.title}</h3>
              <p className="text-gray-700 mb-6">{panel.desc}</p>
              {panel.list && (
                <ul className="space-y-3">
                  {panel.list.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-gray-700">
                      <Icon name={panel.listIcon || 'CheckCircle'} className={`w-5 h-5 ${pt.itemIcon} flex-shrink-0 mt-0.5`} />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {panel.rows && (
                <div className="grid grid-cols-1 gap-4">
                  {panel.rows.map((row) => (
                    <div key={row.bold} className="bg-white p-4 rounded-xl shadow-sm flex items-center gap-3">
                      <Icon name={row.icon} className={`w-6 h-6 ${pt.itemIcon}`} />
                      <span className="text-gray-700">{row.pre}<strong>{row.bold}</strong>{row.post}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export const ImageSplit = ({ section, accent }) => {
  const t = ACCENTS[accent];
  return (
    <section className={sectionBg(section.bg)}>
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className={`bg-white p-4 rounded-3xl shadow-sm${section.bg === 'gray' ? '' : ' border border-gray-100'}`}>
            <img src={section.image} alt={section.imageAlt} className="w-full rounded-2xl" />
          </div>
          <div>
            <span className={`${t.label} font-semibold uppercase tracking-wide text-sm`}>{section.label}</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-6">{section.heading}</h2>
            <div className={`space-y-4${section.tags ? ' mb-8' : ''}`}>
              {section.items.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <CheckCircle className={`w-6 h-6 ${t.check} flex-shrink-0 mt-0.5`} />
                  <div>
                    <h4 className="font-semibold text-gray-900">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {section.tags && (
              <div className="flex flex-wrap gap-3">
                {section.tags.map((tag) => (
                  <span key={tag} className={`${t.tag} px-4 py-2 rounded-full text-sm font-medium`}>{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
